/**
 * @fileoverview App shell for the redesigned student portal. Fixed 256px left
 * sidebar on md+, collapsing to a slim top bar + slide-in drawer on mobile.
 * Wraps the protected student routes as a layout route (renders <Outlet/>).
 * Mirrors the instructor StudioLayout.
 *
 * It holds the portal's one Log practice dialog (StudentPracticeProvider) and
 * reads the teachers' booking pages once for the sidebar and the phone-width
 * plus (C6).
 */

import { useContext, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { StudentAuthContext } from "@/App";
import { practiceLogsEnabled, rehearsalsEnabled } from "@/lib/featureFlags";
import { useStudentBookingLinks } from "@/lib/studentBooking";
import { StudentPracticeProvider, useStudentPractice } from "@/components/StudentPracticeContext";
import { StudentNewMenu } from "@/components/studio/StudentNewMenu";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { StudentSidebarInner } from "@/components/studio/StudentPortalSidebar";
import { Icon } from "@/components/studio/primitives";

const SIDEBAR_W = 256;

export default function StudentPortalLayout() {
  return (
    <StudentPracticeProvider>
      <StudentPortalShell />
    </StudentPracticeProvider>
  );
}

function StudentPortalShell() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { student } = useContext(StudentAuthContext) || {};
  const { openLog } = useStudentPractice();
  const showRehearsals = rehearsalsEnabled(student);
  const showLogs = practiceLogsEnabled(student);
  const bookingLinks = useStudentBookingLinks(!!student);
  const hasNew = showLogs || bookingLinks.length > 0;

  // Reset scroll on navigation (parity with the prototype's goto()).
  useEffect(() => {
    setDrawerOpen(false);
    document.getElementById("student-main")?.scrollTo?.({ top: 0 });
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  return (
    <div className="studio min-h-screen bg-[var(--studio-base)] text-[var(--text-primary)]">
      {/* Desktop rail */}
      <aside
        className="fixed inset-y-0 left-0 z-40 hidden border-r border-[var(--studio-divider)] bg-[var(--studio-sidebar)] md:block"
        style={{ width: SIDEBAR_W }}
      >
        <StudentSidebarInner bookingLinks={bookingLinks} />
      </aside>

      {/* Mobile top bar */}
      <header className="fixed inset-x-0 top-0 z-40 flex h-14 items-center justify-between border-b border-[var(--studio-divider)] bg-[var(--studio-sidebar)] px-4 md:hidden">
        <button
          type="button"
          aria-label="Open menu"
          data-testid="student-menu"
          onClick={() => setDrawerOpen(true)}
          className="flex h-10 w-10 items-center justify-center rounded-[12px] text-[var(--text-secondary)] hover:bg-[var(--studio-raised)]"
        >
          <Icon name="menu" size={22} />
        </button>
        <div className="flex items-center gap-2">
          <img src="/logo192.png" alt="Hans" className="h-7 w-7 rounded-[8px]" />
          <span className="font-sans text-base font-bold text-[var(--text-primary)]">Hans</span>
        </div>
        {hasNew ? (
          <button
            type="button"
            aria-label="Log practice or book a lesson"
            data-testid="student-new"
            onClick={() => setNewOpen(true)}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--accent-yellow)] shadow-[0_4px_16px_var(--accent-yellow-glow)]"
          >
            <Icon name="plus" size={20} strokeWidth={2.4} className="text-[var(--text-inverse)]" />
          </button>
        ) : showRehearsals ? (
          <button
            type="button"
            aria-label="Record rehearsal"
            onClick={() => navigate("/student/rehearsals?record=1")}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--accent-yellow)] shadow-[0_4px_16px_var(--accent-yellow-glow)]"
          >
            <Icon name="mic" size={18} strokeWidth={2.2} className="text-[var(--text-inverse)]" />
          </button>
        ) : (
          /* Spacer keeps the logo centered when there is nothing to start. */
          <div className="h-10 w-10" aria-hidden="true" />
        )}
      </header>

      {/* Mobile drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent
          side="left"
          className="studio w-[256px] border-[var(--studio-divider)] bg-[var(--studio-sidebar)] p-0"
        >
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <StudentSidebarInner onNavigate={() => setDrawerOpen(false)} bookingLinks={bookingLinks} />
        </SheetContent>
      </Sheet>

      <StudentNewMenu
        open={newOpen}
        onOpenChange={setNewOpen}
        canLog={showLogs}
        onLog={() => openLog()}
        bookingLinks={bookingLinks}
      />

      {/* Content */}
      <main id="student-main" className="min-h-screen pt-14 md:ml-64 md:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
